/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { useCallback, useState } from "react";

interface TalonFlowConfig {
	onReady?: () => void;
	onComplete?: (token: string) => void;
	onClosed?: () => void;
	onFailed?: (...args: unknown[]) => void;
}

interface TalonFlow {
	ready: boolean;
	config: TalonFlowConfig;
}

interface TalonGlobal {
	load: (config: { flow: string } & TalonFlowConfig & { onError?: (...args: unknown[]) => void }) => void;
	execute: (flow: string) => void;
	flows: Record<string, TalonFlow>;
}

declare global {
	interface Window {
		talon?: TalonGlobal;
	}
}

export class CaptchaCancelledError extends Error {
	constructor() {
		super("Captcha cancelled by user");
		this.name = "CaptchaCancelledError";
	}
}

type UseTalonReturn = {
	executeCaptcha: () => Promise<string | undefined>;
	isExecuting: boolean;
};

/**
 * Hook to manage Talon CAPTCHA flow.
 * Returns `executeCaptcha` which resolves with a token on success,
 * `undefined` if Talon is not configured, or rejects with
 * `CaptchaCancelledError` if the user dismisses the challenge.
 */
export const useTalon = (flowId?: string): UseTalonReturn => {
	const [isExecuting, setIsExecuting] = useState(false);

	const executeCaptcha = useCallback((): Promise<string | undefined> => {
		return new Promise<string | undefined>((resolve, reject) => {
			if (!flowId || typeof window === "undefined" || !window.talon) {
				resolve(undefined);
				return;
			}

			setIsExecuting(true);

			const handleSuccess = (token: string): void => {
				setIsExecuting(false);
				resolve(token);
			};

			const handleCancel = (): void => {
				setIsExecuting(false);
				reject(new CaptchaCancelledError());
			};

			const handleError = (...args: unknown[]): void => {
				console.error("Talon captcha error", ...args);
				setIsExecuting(false);
				reject(new Error("Captcha verification failed"));
			};

			if (window.talon.flows?.[flowId]) {
				const currentFlow = window.talon.flows[flowId];
				currentFlow.config.onComplete = handleSuccess;
				currentFlow.config.onClosed = handleCancel;
				currentFlow.config.onFailed = handleError;

				if (currentFlow.ready) {
					window.talon.execute(flowId);
				} else {
					currentFlow.config.onReady = (): void => {
						window.talon?.execute(flowId);
					};
				}
			} else {
				window.talon.load({
					flow: flowId,
					onReady: (): void => {
						window.talon?.execute(flowId);
					},
					onComplete: handleSuccess,
					onClosed: handleCancel,
					onError: handleError,
				});
			}
		});
	}, [flowId]);

	return { executeCaptcha, isExecuting };
};
